import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { getReferences } from '../../../../services/SuppliesService';
import { getOptionalSelectionName } from '../../../../utils/optionalSelections';
import { formatMoney } from '../../../../utils/currency';

/**
 * Modal para elegir opcionales antes de agregar el producto a la venta (igual que en sites).
 * Se muestra cuando el producto tiene optionals y maxOptionalSelections.
 */
const OptionalsModal = ({ open, product, locationId, onConfirm, onClose }) => {
  const [selections, setSelections] = useState([]);
  const [references, setReferences] = useState([]);

  const optionals = product?.optionals ?? [];
  const maxOpt = product?.maxOptionalSelections ?? null;
  const canSelectMore = maxOpt == null || selections.length < maxOpt;

  useEffect(() => {
    if (!open || !locationId) {
      setReferences([]);
      return;
    }

    let cancelled = false;

    getReferences(locationId)
      .then((data) => {
        if (!cancelled) {
          setReferences(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReferences([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, locationId]);

  const toggle = (opt) => {
    const refType = opt.referenceType ?? opt.reference_type;
    const refId1 = opt.referenceId1 ?? opt.reference_id_1;
    const exists = selections.some((s) => (s.referenceType ?? s.reference_type) === refType && (s.referenceId1 ?? s.reference_id_1) === refId1);
    if (exists) {
      setSelections((prev) => prev.filter((s) => !((s.referenceType ?? s.reference_type) === refType && (s.referenceId1 ?? s.reference_id_1) === refId1)));
    } else if (canSelectMore) {
      const extraPrice = opt.extraPrice ?? opt.extra_price ?? 0;
      const name = getOptionalSelectionName(opt, references);
      setSelections((prev) => [...prev, { referenceType: refType, referenceId1: refId1, extraPrice: extraPrice, name }]);
    }
  };

  const basePrice = product?.price ?? 0;
  const extraTotal = selections.reduce((sum, s) => sum + (Number(s.extraPrice) || 0), 0);
  const effectiveUnitPrice = basePrice + extraTotal;

  const handleConfirm = () => {
    onConfirm({
      optionalSelections: selections.map((s) => ({
        referenceType: s.referenceType ?? s.reference_type,
        referenceId1: s.referenceId1 ?? s.reference_id_1,
        extraPrice: s.extraPrice ?? 0,
        name: s.name ?? 'Opcional',
      })),
      unitPrice: effectiveUnitPrice,
    });
    setSelections([]);
    onClose();
  };

  const handleClose = () => {
    setSelections([]);
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{product.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Elegí los opcionales que quieras agregar (opcional).
        </Typography>
        {optionals.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Opcionales {maxOpt != null ? `(elegir hasta ${maxOpt})` : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {optionals.map((opt) => {
                const refType = opt.referenceType ?? opt.reference_type;
                const refId1 = opt.referenceId1 ?? opt.reference_id_1;
                const extraPrice = opt.extraPrice ?? opt.extra_price ?? 0;
                const name = getOptionalSelectionName(opt, references);
                const selected = selections.some((s) => (s.referenceType ?? s.reference_type) === refType && (s.referenceId1 ?? s.reference_id_1) === refId1);

                return (
                  <Box
                    key={`${refType}-${refId1}`}
                    sx={{
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%' }}
                      control={
                        <Checkbox
                          checked={selected}
                          onChange={() => toggle(opt)}
                          disabled={!selected && !canSelectMore}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          <Typography variant="body1">{name}</Typography>
                          {extraPrice > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              + {formatMoney(Number(extraPrice))}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                );
              })}
            </Box>
            {selections.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Total con opcionales: {formatMoney(effectiveUnitPrice)}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Agregar a la venta
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptionalsModal;
